import { Button, Dropdown, DropdownContent, DropdownItem, DropdownTrigger, Stack } from 'component-library';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PlotWithFacts } from '@/api/rest/resources/types/plot';
import { Card } from '@/components/widgets/o/Card/Card';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { useAllPlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';
import { buildPath } from '@/utils/buildPath';

export const PlotsDropdown = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('navbar.plotOverview')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Widget rendering error: ${error} ${info}`);
      }}
    >
      <PlotsDropdownBase />
    </ErrorBoundary>
  );
};

const PlotsDropdownBase = () => {
  const plots = useAllPlotsForOrganization().data?.results ?? [];
  const [selectedPlot, setSelectedPlot] = useState<PlotWithFacts | null>(null);
  const paths = usePathSet();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const handlePlotSelect = (plot: PlotWithFacts) => {
    setSelectedPlot(plot);

    const destinationPath = buildPath(paths.uploadsPlotOverviewPlot, {
      pathParams: { plotId: plot.id },
    });
    navigate(destinationPath, {
      state: {
        previousPath: paths.uploadsPlotOverview,
      },
    });
  };

  if (plots.length === 0) {
    return (
      <span className='typography-body1 text-text-secondary'>
        {t('shared.uploadsPlotOverview.plotsDropdown.noPlots')}
      </span>
    );
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <Button
          data-testid='plot-dropdown-trigger'
          variant='outline'
          className='w-fit border-neutral-black-12 rounded-xl text-text-primary'
        >
          {selectedPlot?.name ?? t('shared.uploadsPlotOverview.plotsDropdown.placeholder')}
          <i className='ri-arrow-down-s-line' />
        </Button>
      </DropdownTrigger>
      <DropdownContent>
        {plots.map((plot) => (
          <DropdownItem key={plot.id} onClick={() => handlePlotSelect(plot)}>
            {plot.name}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
};
